import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import { GetYearlySales, GetYearlyPl } from '../store/slices/ledger';
import { useDispatch } from 'react-redux';
import Loader from '../common/Loader';

// Utility function to format amounts
const formatCurrency = (amount) => {
  if (!amount) {
    return '₹0.00'; // Default value for invalid or missing data
  }
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const FPOLifecycle = () => {
  const dispatch = useDispatch();
  const user = JSON.parse(localStorage.getItem('user'));
  const id = user?.data?.profile_id; // profile_id

  const [isLoading, setIsLoading] = useState(false);
  const [yearlySales, setYearlySales] = useState({});
  const [yearlyPnl, setYearlyPnl] = useState({});

  const fetchYearlySales = async () => {
    try {
      const data = await dispatch(GetYearlySales({ profile_id: id })).unwrap();
      setYearlySales(data?.data || {});
      console.log('fetchYearlySales', data?.data);
    } catch (error) {
      console.error('Error fetching yearly sales:', error.message);
    }
  };

  const fetchYearlyPnl = async () => {
    try {
      const data = await dispatch(GetYearlyPl({ profile_id: id })).unwrap();
      setYearlyPnl(data?.data || {});
      console.log('fetchYearlyPnl', data?.data.first_3_years?.net_profit_or_loss?.amount);
    } catch (error) {
      console.error('Error fetching yearly P&L:', error.message);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await Promise.all([fetchYearlySales(), fetchYearlyPnl()]);
      setIsLoading(false);
    };
    fetchData();
  }, [dispatch, id]);

  const stages = [
    {
      color: 'success', // Stage 1
      details: [
        { title: 'Stage: 1', subtitle: 'First 3 Years' },
        { label: 'Members', value: '0' },
        { label: 'Sales', value: formatCurrency(yearlySales?.first_three_years_sales) },
        {
          label: `${yearlyPnl?.first_3_years?.net_profit_or_loss?.description || ''}`,
          value: `${
            Number(String(yearlyPnl?.first_3_years?.net_profit_or_loss?.amount || '').replace(/,/g, '')) === 0
              ? formatCurrency(Number(String(yearlyPnl?.first_3_years?.total_income || '').replace(/,/g, '')) - Number(String(yearlyPnl?.first_3_years?.total_expenses || '').replace(/,/g, '')))
              : formatCurrency(Number(String(yearlyPnl?.first_3_years?.net_profit_or_loss?.amount || '').replace(/,/g, '')))
          }`,
        },

        { label: 'Management cost', value: '0' },
        { label: 'Equity Grant', value: '0' },
      ],
    },
    {
      color: 'dark', // Stage 2
      details: [
        { title: 'Stage: 2', subtitle: 'Next 2 Years' },
        { label: 'Members', value: '0' },
        { label: 'Sales', value: formatCurrency(yearlySales?.next_two_years_sales) },
        {
          label: `${yearlyPnl?.next_2_years?.net_profit_or_loss?.description || ''}`,
          value: `${
            Number(yearlyPnl?.next_2_years?.net_profit_or_loss?.amount) === 0
              ? formatCurrency(Number(String(yearlyPnl?.next_2_years?.total_income || '').replace(/,/g, '')) - Number(String(yearlyPnl?.next_2_years?.total_expenses || '').replace(/,/g, '')))
              : formatCurrency(Number(String(yearlyPnl?.next_2_years?.net_profit_or_loss?.amount || '').replace(/,/g, '')))
          }`,
        },

        { label: 'Management cost', value: '0' },
        { label: 'Equity Grant', value: '0' },
      ],
    },
    {
      color: 'brown', // Stage 3
      details: [
        { title: 'Stage: 3', subtitle: 'Overall' },
        { label: 'Members', value: '0' },
        { label: 'Sales', value: formatCurrency(yearlySales?.overall_sales) },
        {
          label: `${yearlyPnl?.overall?.net_profit_or_loss?.description || ''}`,
          value: `${
            Number(yearlyPnl?.overall?.net_profit_or_loss?.amount) === 0
              ? formatCurrency(Number(String(yearlyPnl?.overall?.total_income || '').replace(/,/g, '')) - Number(String(yearlyPnl?.overall?.total_expenses || '').replace(/,/g, '')))
              : formatCurrency(Number(String(yearlyPnl?.overall?.net_profit_or_loss?.amount || '').replace(/,/g, '')))
          }`,
        },

        { label: 'Management cost', value: '0' },
        { label: 'Equity Grant', value: '0' },
      ],
    },
  ];

  return (
    <AdminLayout>
      {isLoading && <Loader />}
      <div className="row content-body">
        <div className="container-fluid">
          <div className="page-header">
            <div>
              <h2 className="main-content-title tx-24 mg-b-5">FPO Lifecycle</h2>
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <a href="#">FPO Lifecycle</a>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                Lifecycle
                </li>
              </ol>
            </div>
          </div>
          <div className="container my-4">
            {stages.map((stage, stageIndex) => (
              <div className="row" key={stageIndex} style={{ marginBottom: '3rem', marginTop: '3rem' }}>
                {/* Stage Card */}
                <div className="col-md-2 mb-3">
                  <div className={`card text-white bg-${stage.color} h-100 shadow`} style={{ background: stage.color }}>
                    <div className="card-body text-center py-3">
                      <h5 className="title">{stage.details[0].title}</h5>
                      <small className="mt-1 d-block">{stage.details[0].subtitle}</small>
                    </div>
                  </div>
                </div>

                {/* Details Cards */}
                {stage.details.slice(1).map((detail, detailIndex) => (
                  <div className="col-md-2 col-6 mb-3" key={detailIndex}>
                    <div className="card h-100 shadow-sm text-center">
                      <div className="card-body py-3">
                        <h6 className="card-title text-muted">{detail.label}</h6>
                        <p className="card-text fw-bold">{detail.value}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default FPOLifecycle;
